/**
 * Lobby Button
 * Leaves the table and goes to the lobby. Leaving a game you're still in
 * gives it up, so that takes a second tap ("Leave game?") within a few
 * seconds; with `confirm` off (the game's already over) one tap does it.
 */

import React, { useEffect, useState } from 'react';

interface LobbyButtonProps {
  onLeave: () => Promise<void>;
  confirm?: boolean;
  className?: string;
}

const LobbyButton: React.FC<LobbyButtonProps> = ({ onLeave, confirm = true, className = '' }) => {
  const [armed, setArmed] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // An unanswered "Leave game?" goes back to "Lobby".
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  return (
    <button
      data-testid="lobby"
      onClick={async () => {
        if (confirm && !armed) {
          setArmed(true);
          return;
        }
        setLeaving(true);
        await onLeave();
      }}
      disabled={leaving}
      className={`rounded-xl border font-bold transition-all disabled:opacity-60 ${
        armed
          ? 'border-rose-500 bg-rose-900/60 text-rose-100'
          : 'border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700'
      } ${className}`}
    >
      {leaving ? 'Leaving…' : armed ? 'Leave game?' : 'Lobby'}
    </button>
  );
};

export default LobbyButton;
